import { ExpandableDialog } from "@components/ExpandableDialog";
import { Palette } from "@mui/material";
import { IChatBlock } from "@pages/askDost/types";
import { isPieOrDonutChart } from "@pages/askDost/utils/constants";
import { ApexOptions } from "apexcharts";
import numeral from "numeral";
import React, { useMemo } from "react";
import ReactApexChart, {
  Props as IReactApexChartProps,
} from "react-apexcharts";

interface IChartBlockProps {
  block: IChatBlock;
  palette: Palette;
  translate: (text: string, options?: any) => string;
}

type TApexChartType = NonNullable<IReactApexChartProps["type"]>;

const normalizeChartType = (chartType: string | undefined): TApexChartType => {
  if (chartType === "doughnut") return "donut";
  if (chartType === "horizontal_bar") return "bar";
  return (chartType as TApexChartType) || "line";
};

export const ChartBlock: React.FC<IChartBlockProps> = ({
  block,
  palette,
  translate,
}) => {
  const spec = block?.spec;
  const chartType = normalizeChartType(spec?.chart_type);

  const isPieOrDonut = isPieOrDonutChart(chartType);
  const isHeatmap = spec?.chart_type === "heatmap";

  const { series, options, hasValidData } = useMemo(() => {
    if (!Array.isArray(spec?.series) || spec?.series?.length === 0) {
      return { series: [], options: {}, hasValidData: false };
    }

    const hasAnyData = spec?.series?.some(
      (s) => s?.data && Array.isArray(s.data) && s.data.length > 0,
    );

    if (!hasAnyData) {
      return { series: [], options: {}, hasValidData: false };
    }

    let processedSeries: any;
    if (isPieOrDonut) {
      const firstSeries = spec?.series?.[0];
      if (
        firstSeries &&
        typeof firstSeries === "object" &&
        Array.isArray(firstSeries.data) &&
        firstSeries.data.length > 0
      ) {
        processedSeries = [...firstSeries.data];
      } else {
        processedSeries = [];
      }

      if (!Array.isArray(processedSeries) || processedSeries.length === 0) {
        return { series: [], options: {}, hasValidData: false };
      }
    } else {
      const validSeries =
        spec?.series
          ?.filter(
            (s) => s && s.name && Array.isArray(s.data) && s.data.length > 0,
          )
          ?.map((s) => ({ ...s, data: [...s.data] })) ?? [];

      if (validSeries.length === 0) {
        return { series: [], options: {}, hasValidData: false };
      }
      processedSeries = validSeries;
    }

    const chartOptions: ApexOptions = {
      chart: {
        type: chartType,
        toolbar: { show: false },
        zoom: {
          enabled: false,
          allowMouseWheelZoom: false,
        },
        background: "transparent",
      },
      legend: {
        show: isHeatmap ? false : true,
      },
      labels: spec?.labels ? [...spec.labels] : undefined,
      dataLabels: {
        formatter: isPieOrDonut
          ? (_val, opts) =>
              `${numeral(processedSeries[opts.seriesIndex] ?? 0).format("0a")}`
          : (val) => `${numeral(val).format("0a")}`,
        style: {
          colors: [
            (_opts) => {
              return palette.text.label;
            },
          ],
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => `${numeral(val).format("0a")}`,
        },
      },
      tooltip: {
        y: {
          formatter: (value) => numeral(value).format("0a"),
        },
      },
      ...(isPieOrDonut
        ? {}
        : {
            grid: {
              borderColor: (palette as any).dostStone,
            },
          }),
      ...(isHeatmap
        ? {
            plotOptions: {
              heatmap: {
                colorScale: {
                  ranges: [
                    {
                      from: 0,
                      to: 0,
                      color: "#E0E0E0",
                    },
                  ],
                },
              },
            },
          }
        : {}),
    } as ApexOptions;

    return {
      series: processedSeries,
      options: chartOptions,
      hasValidData: true,
    };
  }, [spec, isPieOrDonut, isHeatmap, palette, chartType]);

  if (!hasValidData || !series || series.length === 0) {
    return null;
  }

  return (
    <ExpandableDialog title={spec?.title || ""} translate={translate}>
      <ReactApexChart
        type={chartType}
        series={series}
        options={options as ApexOptions}
        height={280}
        width="100%"
      />
    </ExpandableDialog>
  );
};
